// hooks/useVideoComments.ts — reactive hook for video comments (client-only, in-memory).
//
// Reads the comment list for a given videoId from videoCommentsStore reactively and
// exposes the mutations components need. Components must NOT import
// videoCommentsStore directly. Deliberately separate from useComments (post
// comments) — no shared store, no shared service, per the owner's "don't couple
// to the post-comment system" decision.

import { useCallback } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useVideoCommentsStore } from '@/core/stores/videoCommentsStore';
import type { VideoComment } from '@/core/models/videoComment';

export interface AddVideoCommentFields {
  videoId: string;
  authorId: string;
  body: string;
}

export interface AddVideoReplyFields {
  videoId: string;
  authorId: string;
  /** Must be a TOP-LEVEL comment id — the flatten rule. */
  parentId: string;
  body: string;
  /** Set only when replying to a nested comment (visual attribution). */
  replyingToHandle?: string | null;
}

export interface VideoCommentsHook {
  /** All comments for the video (any nesting level), newest-first. */
  comments: VideoComment[];
  /** Top-level comments (parentId === null). */
  topLevel: VideoComment[];
  /** Direct replies to a given top-level comment id. */
  repliesFor: (parentId: string) => VideoComment[];
  /** Add a top-level comment. */
  addComment: (fields: AddVideoCommentFields) => VideoComment;
  /** Add a nested reply (parentId must be a top-level comment id). */
  addReply: (fields: AddVideoReplyFields) => VideoComment;
  /** Toggle the liked state of a comment (±1). */
  toggleLike: (id: string) => void;
}

export function useVideoComments(videoId: string): VideoCommentsHook {
  // useShallow prevents re-renders when unrelated (other-video) comments change —
  // see hooks/useComments.ts for the same pattern on post comments.
  const comments = useVideoCommentsStore(
    useShallow((s) => s.comments.filter((c) => c.videoId === videoId)),
  );
  const addComment = useVideoCommentsStore((s) => s.addComment);
  const patchComment = useVideoCommentsStore((s) => s.patchComment);

  const topLevel = comments.filter((c) => c.parentId === null);

  const repliesFor = useCallback(
    (parentId: string) => comments.filter((c) => c.parentId === parentId),
    [comments],
  );

  const addTopLevel = useCallback(
    (fields: AddVideoCommentFields): VideoComment => {
      const comment: VideoComment = {
        id: crypto.randomUUID(),
        videoId: fields.videoId,
        authorId: fields.authorId,
        parentId: null,
        replyingToHandle: null,
        body: fields.body,
        time: 'now',
        likes: 0,
        liked: false,
        deleted: false,
        edited: false,
      };
      addComment(comment);
      return comment;
    },
    [addComment],
  );

  const addReply = useCallback(
    (fields: AddVideoReplyFields): VideoComment => {
      const comment: VideoComment = {
        id: crypto.randomUUID(),
        videoId: fields.videoId,
        authorId: fields.authorId,
        parentId: fields.parentId,
        replyingToHandle: fields.replyingToHandle ?? null,
        body: fields.body,
        time: 'now',
        likes: 0,
        liked: false,
        deleted: false,
        edited: false,
      };
      addComment(comment);
      return comment;
    },
    [addComment],
  );

  const toggleLike = useCallback(
    (id: string) => {
      const current = comments.find((c) => c.id === id);
      if (!current) return;
      patchComment(id, {
        liked: !current.liked,
        likes: current.liked ? current.likes - 1 : current.likes + 1,
      });
    },
    [comments, patchComment],
  );

  return { comments, topLevel, repliesFor, addComment: addTopLevel, addReply, toggleLike };
}
